import classNames from 'classnames';
import classnames from 'classnames';
import React, { FC, useState, useCallback, useEffect, useRef } from 'react';

import Icon, { IconProps } from '../Icon';
import noop from '@wetransfer/transfer/oneui/core/utils/noop';

import styles from './Input.module.scss';
import InputMessage, { InputMessageProps } from './InputMessage';

export type InputProps = Partial<InputMessageProps> & {
  /**
    When creating a controlled input, do not pass `defaultValue`
  */
  value?: string;
  /**
    When creating an uncontrolled input, do not pass `value`
  */
  defaultValue?: string;
  /**
    triggered on every change even of the input
  */
  onChange?: (value: string) => void;
  /**
    triggered on when the user is no longer focusing on the input, and only if
    the value has changed.
  */
  onBlur?: (value: string) => void;
  iconName?: IconProps['name'];
  iconAlt?: IconProps['alt'];
  className?: string;
  htmlProps?: Record<string, string | number>;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  testid?: string;
  isDropdown?: boolean;
  isFocus?: boolean;
  enableTabIndex?: boolean;
  onkeydown?: (value: string) => void;
  additionalClass?: string;
};

const Input: FC<InputProps> = ({
  onChange = noop,
  onBlur = noop,
  defaultValue,
  value,
  iconName,
  iconAlt,
  className,
  htmlProps,
  placeholder,
  label,
  message,
  disabled = false,
  isDropdown = false,
  isFocus = false,
  enableTabIndex = true,
  onkeydown = noop,
  additionalClass,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  // We want to know if there has actually been a change to this input since it was rendered
  const [hasChanged, setHasChanged] = useState(false);

  const inputRef = useRef(null);
  useEffect(() => {
    if (isFocus) {
      setIsFocused(true);
      inputRef.current.focus();
    }
  }, [isFocus]);

  const handleBlur = useCallback(
    (e) => {
      setIsFocused(false);

      if (hasChanged) {
        onBlur(e.target.value);
      }
    },
    [hasChanged, onBlur]
  );

  const handleChange = useCallback(
    (e) => {
      onChange(e.target.value);
      setHasChanged(true);
    },
    [setHasChanged, onChange]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && onkeydown) {
        e.preventDefault();
        onkeydown(e.target.value);
        setHasChanged(true);
      }
    },
    [setHasChanged, onkeydown]
  );
  const valueProp = defaultValue ? { defaultValue } : { value };

  const containerClassNames = classNames([styles.container, className], {
    [styles.containerWithLabel]: label,
    [styles.containerWithFocus]: isFocused,
    [styles.containerWithMessage]: Boolean(message),
    [styles[`containerWithMessage${message?.type || '_'}`]]: Boolean(message),
    [styles.containerDisabled]: disabled,
    [styles.containerWithValue]: defaultValue,
  });

  const inputClassNames = classNames([styles.input], {
    [styles.inputDropdown]: isDropdown,
  });

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const labelClasses = classnames(styles.inputLabel, {
    [additionalClass]: additionalClass,
  });

  return (
    <label
      className={containerClassNames}
      data-testid="OneUI-Input"
      {...htmlProps}
    >
      <div className={styles.inputWrapper}>
        {label && <span className={labelClasses}>{label}</span>}

        {iconName && (
          <div className={styles.iconContainer}>
            <Icon
              name={iconName}
              alt={iconAlt || `${iconName} icon`}
              size={20}
            />
          </div>
        )}

        <input
          {...valueProp}
          ref={inputRef}
          className={inputClassNames}
          onChange={handleChange}
          placeholder={placeholder}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          data-testid={'input'}
          readOnly={isDropdown}
          tabIndex={!enableTabIndex ? -1 : 0}
          onKeyDown={handleKeyDown}
        />
      </div>

      {message && <InputMessage message={message} />}

      {disabled && (
        <Icon
          className={styles.disabledLockIcon}
          name="lock"
          alt="disabled input lock icon"
          size={12}
        />
      )}

      {isDropdown && (
        <Icon
          name="arrow-down"
          alt="icon dropdown"
          className={styles.dropdownIcon}
          size={12}
        />
      )}
    </label>
  );
};

export default Input;
